// =========================================================================
// Fehlerberichte an den Server
// =========================================================================
// Ein Fehler im Browser landete bisher nur in der Konsole des Nutzers. Wer
// keinen Screenshot schickt, meldet "geht nicht" — und niemand kann es
// nachvollziehen. Genau dieser Fall hat uns Wochen gekostet.
//
// Dieses Modul faengt ab:
//   - window.onerror            (nicht abgefangene JS-Fehler)
//   - unhandledrejection        (abgebrochene Promises)
//   - fehlgeschlagene API-Aufrufe (ueber reportApiError aus api.js)
// und schickt sie an POST /api/logs/client, wo sie im selben Log landen wie
// die Serverfehler.
//
// Grundsaetze:
//   - Darf die App nie stoeren: alles in try/catch, Versand per sendBeacon
//     oder keepalive-fetch, Antwort wird ignoriert.
//   - Darf sich nie selbst ausloesen: Fehler beim Senden werden verschluckt.
//   - Drosselung gegen Render-Schleifen, die sonst Log und Netz fluten.

import { getBaseUrl } from "../config/apiConfig";

const ENDPOINT = () => `${getBaseUrl()}/api/logs/client`;
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

// Drosselung: hoechstens 10 Berichte pro Minute, identische Meldungen nur
// einmal pro Minute. Eine Komponente in einer Fehlerschleife erzeugt sonst
// hunderte identische Zeilen.
const MAX_PER_MINUTE = 10;
const WINDOW_MS = 60 * 1000;
let sentTimestamps = [];
const recentMessages = new Map();

function allowed(message) {
  const now = Date.now();
  sentTimestamps = sentTimestamps.filter((t) => now - t < WINDOW_MS);
  if (sentTimestamps.length >= MAX_PER_MINUTE) return false;

  const last = recentMessages.get(message);
  if (last && now - last < WINDOW_MS) return false;

  sentTimestamps.push(now);
  recentMessages.set(message, now);
  if (recentMessages.size > 50) {
    // Aeltesten Eintrag entfernen, damit die Map nicht waechst.
    recentMessages.delete(recentMessages.keys().next().value);
  }
  return true;
}

// Der Nutzername liegt nur im LoginContext, nicht im localStorage. Der Context
// meldet ihn hierher, sobald er bekannt ist — ohne diese Zuordnung ist ein
// Fehlerbericht im Log kaum einem Vorgang zuzuordnen.
let reportContext = { username: undefined };

export function setReportContext(ctx) {
  reportContext = { ...reportContext, ...ctx };
}

function currentUsername() {
  return reportContext.username;
}

export function reportError(payload) {
  try {
    const message = String(payload?.message ?? "").slice(0, 2000);
    if (!message || !allowed(message)) return;

    const body = JSON.stringify({
      level: payload.level || "error",
      kind: payload.kind || "error",
      message,
      stack: payload.stack ? String(payload.stack).slice(0, 4000) : undefined,
      url: window.location?.href,
      appVersion: APP_VERSION,
      username: currentUsername(),
      relatedRequestId: payload.relatedRequestId,
      status: payload.status,
    });

    // sendBeacon ueberlebt auch einen Tab-Wechsel oder das Schliessen der
    // Seite — gerade beim Absturz der wahrscheinlichste Moment.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT(), new Blob([body], { type: "application/json" }));
      return;
    }

    fetch(ENDPOINT(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Ein Fehler im Fehler-Reporter darf nichts ausloesen.
  }
}

// Von api.js aufgerufen, wenn ein Request fehlschlaegt. requestId stammt aus
// dem X-Request-Id-Header der Antwort und verbindet den Client-Fehler mit der
// serverseitigen Logzeile desselben Requests.
export function reportApiError({ url, status, message, requestId }) {
  reportError({
    kind: "api",
    level: status && status >= 500 ? "error" : "warn",
    message: `${status || "network"} ${url} — ${message}`,
    status,
    relatedRequestId: requestId,
  });
}

let installed = false;

export function installErrorReporter() {
  if (installed) return;
  installed = true;

  window.addEventListener("error", (event) => {
    // Fehler beim Laden von Ressourcen (img, script) haben kein `error`-Objekt.
    if (!event?.message && !event?.error) return;
    reportError({
      kind: "error",
      message: event.message || String(event.error),
      stack: event.error?.stack,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    reportError({
      kind: "unhandledrejection",
      message: reason?.message || String(reason),
      stack: reason?.stack,
    });
  });
}

const errorReporter = { installErrorReporter, reportError, reportApiError, setReportContext };
export default errorReporter;
